<template>
  <v-app id="slick-cactus-app" :class="[{ 'ov-hidden': isLoading }, {'devmode': $devmode}]">
    <!-- Status alerts are solid colour slabs by default. On the dark theme the
         light status colours under white text drop to ~2.4:1, so alerts without
         their own `variant` turn tonal there (coloured text on a tinted ground);
         the light theme keeps the solid look. -->
    <v-defaults-provider :defaults="themeDefaults">
      <div class="notification-wrapper">
        <AppNotification
          v-for="(item, key, index) in notifications"
          :key="key"
          :index="index"
          :notification-id="key"
          :notification="item"
        />
      </div>
      <SplashScreen v-if="isLoading" />
      <router-view v-else />
      <v-dialog
        width="400"
        :model-value="isOutlookExpired && !outlookProcessed"
        persistent
      >
        <MemberOutlookExpired
          @outlook:processed="outlookProcessed = true"
        />
      </v-dialog>
      <v-snackbar
        v-model="snackbar.open"
        color="info"
        location="bottom"
        :timeout="-1"
        class="app-snackbar"
      >
        <div v-html="snackbar.message" />
        <v-btn variant="text" @click="closeSnackbarHandler"> Close </v-btn>
      </v-snackbar>
      <v-snackbar
        v-model="swUpdate.snackbar"
        color="primary"
        location="bottom"
        :timeout="-1"
        class="sw-update-snackbar"
      >
        A new version is available.
        <template #actions>
          <v-btn
            variant="text"
            @click="refreshAppSw"
          >
            Update
          </v-btn>
        </template>
      </v-snackbar>
      <!-- the one help / request panel (the sidebar button and error notifications open it); it
           slides up from the bottom, and a stray click outside cannot drop a half-written request -->
      <v-dialog
        v-model="dialog"
        fullscreen
        persistent
        transition="dialog-bottom-transition"
      >
        <TrackerWrapper />
      </v-dialog>
      <v-dialog
        v-model="emailVerifyDialog"
        width="400"
      >
        <v-card class="pt-4">
          <v-card-text class="text-center">
            <v-icon size="x-large">mdi-email-seal-outline</v-icon>
            <div class="title">Email Confirmed</div>
            <p class="subtitle-2">Your email address has been successfully confirmed</p>
            <v-btn @click="emailVerifyDialog = false">Close</v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
      <OTPRequireDialog v-if="showOtp" />
      <TemporaryPasswordDialog v-if="showTemporaryPasswordDialog" />
      <AiAgentWidget v-if="showAgent" v-model="isAiAgentOpen" />
    </v-defaults-provider>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import { RootActions } from '@/store/types/action-types';
import { GET_USER_SETTINGS } from '@/store/modules/settings/settings-actions-types';
import { subscribeToNotifications } from '@/utils/firebase';
import { SubscriptionsActions } from '@/store/modules/subscriptions/subscription-types';
import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { MapStylesMixin } from '@/bundles/Map/mixins/MapStylesMixin';
import { parseJwt } from '@/bundles/App/helpers/parseJWT';
import { RootMutations } from '@/store/types/mutation-types';
import { initSocket } from '@/plugins/socket/socket';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import AppNotification from '@/bundles/App/components/AppNotification.vue';
import MemberOutlookExpired from '@/bundles/Members/components/MemberOutlookExpired.vue';
import OTPRequireDialog from '@/bundles/User/components/OTPRequireDialog.vue';
import TemporaryPasswordDialog from '@/bundles/Auth/components/TemporaryPasswordDialog.vue';
import AiAgentWidget from '@/bundles/AiAgent/components/AiAgentWidget.vue';
import TrackerWrapper from '@/components/common-components/tracker/TrackerWrapper.vue';
import SplashScreen from '@/components/SplashScreen.vue';

import { getLocaleMessages } from './bundles/Localization/messages';
import { defaultMapStyle } from './bundles/Map/helpers/defaultMapStyle';
import { GET_LISTS } from './store/modules/admin/admin-types';
import { eventBus } from './eventbus/eventbus';
import { TOGGLE_SPLASH_SCREEN, TOGGLE_SNACKBAR } from './store/action-types';

export default {
  name: 'App',

  components: {
    OTPRequireDialog,
    TemporaryPasswordDialog,
    SplashScreen,
    TrackerWrapper,
    AppNotification,
    MemberOutlookExpired,
    AiAgentWidget,
  },

  mixins: [MapStylesMixin, AccountLayoutMixin],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: () => ({
    dialog: false,
    feedbackInfo: null,
    isUpdateAppWindowShown: false,
    swUpdate: {
      snackbar: false,
      refreshing: false,
      registration: null,
    },
    outlookProcessed: false,
    emailVerifyDialog: false,
    isAiAgentOpen: false,
  }),

  computed: {
    themeDefaults () {
      return this.$vuetify.theme.current.dark ? { VAlert: { variant: 'tonal' } } : {};
    },
    ...mapState({
      user: (state) => state.user,
      token: (state) => state.idToken,
      userId: (state) => state.userId,
      isMultipleAccountAuth: (state) => state.isMultipleAccountAuth,
      notifications: (state) => state.Notifications.notifications,
      isLoading: (state) => state.splash,
      snackbar: (state) => state.snackbar,
      userIntegration: (state) => state.userIntegrations,
      activeAccountId: (state) => state.activeAccount?.id,
      otpRequireDialog: (state) => state.otpRequireDialog,
      isPasswordTemporary: (state) => state.isPasswordTemporary,
    }),
    ...mapGetters('Admin', ['getListByCategory']),
    isOutlookExpired: function () {
      return this.userIntegration.some((integration) => integration.name === 'outlook' && integration.status === 'expired');
    },
    showOtp: function () {
      // Temporary solution, we need to skip that dialog for astra accounts
      if (['redteam@getastra.com', 'redteam1@getastra.com', 'redteam2@getastra.com'].includes(this.user?.email)) {
        return false;
      }
      // not on the sign-in pages or under the splash: sign-in loads the user, then reloads into the
      // app, so the dialog would flash there and open again after the reload
      const onSignInPage = this.$route.matched.some((record) => record.meta.requiresTokenAbsence);
      return this.user && !this.user?.otp_enabled && !onSignInPage && !this.isLoading;
    },
    showAgent: function () {
      return !this.isLoading && [2, 91, 92, 436, 602, 135].includes(this.userId);
    },
    showTemporaryPasswordDialog: function () {
      return this.user && this.isPasswordTemporary && this.$route.name !== 'user-security';
    },
  },

  watch: {
    $route (to, from) {
      if (to.query.logout || from.query.logout) {
        this.$store.dispatch(RootActions.LOG_OUT);
      }
      if (from.name === 'welcome') {
        this.toggleScreen(false);
      }
    },
    isMultipleAccountAuth: {
      handler: function (value) {
        if (value) {
          this.$router.push({ name: 'my-accounts' });
        }
      },
      immediate: true,
    },
    token: {
      handler: async function (value) {
        const parsedToken = parseJwt(value);
        this.setUserRoles(parsedToken?.scope || []);
        this.setUserPermissions(parsedToken?.permissions || []);
        initSocket(value);
      },
      immediate: true,
    },
    selectedStyle: {
      handler (value) {
        if (value) {
          this.setMapStyle(value);
        }
      },
      immediate: true
    },
    defaultStyle (value) {
      if (!value) {
        this.setMapStyle(defaultMapStyle.value);
      }
    }
  },

  created: function () {
    eventBus.$on('updateAppVersionNoModal', this.updateAppVersionNoModal);
    this.$eventBus.$on('open-request-form', this.openRequestForm);
    this.$eventBus.$on('closeFeedbackForm', this.closeFormHandler);
    this.$eventBus.$on('open:ai-agent', this.openAiAgent);

    document.addEventListener(
      'sw:updated',
      this.showRefreshUI,
      { once: true }
    );

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        'controllerchange', () => {
          if (this.swUpdate.refreshing) return;
          this.swUpdate.refreshing = true;
          window.location.reload();
        }
      );
    }
  },

  beforeMount () {
    if (!this.token) {
      this.toggleScreen(false);
      return;
    }
    this.fetchAllSettings();
  },

  updated: function () {
    const propertyTypes = this.getListByCategory('property_types').items;

    if (!propertyTypes.length && this.token) {
      this.list({ token: this.token });
    }
  },

  mounted: function () {
    if (this.$route.query.verify_email) {
      this.verifyEmail();
    }
  },

  beforeUnmount () {
    eventBus.$off('updateAppVersionNoModal', this.updateAppVersionNoModal);
    this.$eventBus.$off('closeFeedbackForm', this.closeFormHandler);
    this.$eventBus.$off('open-request-form', this.openRequestForm);
    this.$eventBus.$off('open:ai-agent', this.openAiAgent);
  },

  methods: {
    ...mapActions({
      fetchUser: RootActions.FETCH_CURRENT_USER,
      fetchAccountSettings: RootActions.FETCH_ACCOUNT_SETTINGS,
      fetchUserIntegrations: RootActions.FETCH_USER_INTEGRATIONS,
      fetchAccountPreferences: RootActions.FETCH_ACCOUNT_PREFERENCES,
    }),
    ...mapActions('Admin', {
      list: GET_LISTS,
    }),
    ...mapActions('Settings', {
      fetchSettings: GET_USER_SETTINGS,
    }),
    ...mapActions('Subscriptions', {
      getSubscriptions: SubscriptionsActions.GET_SUBSCRIPTIONS,
    }),
    ...mapMutations('Notifications', ['ADD_NOTIFICATION']),
    ...mapMutations({
      toggleScreen: TOGGLE_SPLASH_SCREEN,
      toggleSnackbar: TOGGLE_SNACKBAR,
      setUserRoles: RootMutations.SET_USER_ROLES,
      setUserPermissions: RootMutations.SET_USER_PERMISSIONS,
    }),
    ...mapMutations({
      updateAppVersion: RootMutations.UPDATE_APP_VERSION
    }),
    verifyEmail: async function () {
      if (!this.$route.query.verifier) {
        window.history.replaceState({}, document.title, `/${this.user?.active_account?.id}/dashboard`);
        return;
      }
      try {
        await authService.verifyEmail(this.$route.query.verifier);
        this.emailVerifyDialog = true;
      } catch (error) {
        const notification = { ...error };
        this.ADD_NOTIFICATION(notification);
      } finally {
        window.history.replaceState({}, document.title, `/${this.user?.active_account?.id}/dashboard`);
      }
    },
    openRequestForm: function () {
      this.dialog = true;
    },
    openAiAgent: function () {
      this.isAiAgentOpen = true;
    },
    showRefreshUI (event) {
      this.swUpdate.registration = event.detail;
      this.swUpdate.snackbar = true;
    },
    refreshAppSw () {
      this.swUpdate.snackbar = false;
      if (!this.swUpdate.registration || !this.swUpdate.registration.waiting) { return; }
      this.swUpdate.registration.waiting.postMessage('skipWaiting');
      this.swUpdate.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    },
    closeSnackbarHandler: function () {
      this.toggleSnackbar({
        open: false,
        message: '',
      });
    },
    closeFormHandler () {
      this.dialog = false;
    },
    updateAppVersionNoModal (newVersion) {
      this.updateAppVersion(newVersion);
    },
    /**
     * Handler for the success event
     * */
    async fetchAllSettings () {
      try {
        this.toggleScreen(true);

        // The settings below need only the token, the user id and the active account, all kept from the last
        // session, so they load alongside /auth/me instead of after it. Without a kept account (first sign-in)
        // they still wait for the user.
        const userPromise = this.user ? null : this.fetchUser();
        if (userPromise && !this.activeAccountId) {
          await userPromise;
        }

        const settingsPromises = [
          userPromise,
          this.list({ token: this.token }),
          this.fetchSettings(this.userId).then(() => {
            subscribeToNotifications();
          }),
          this.fetchAccountSettings(),
          this.fetchAccountPreferences(),
          this.fetchUserIntegrations(),
          this.getSubscriptions(),
        ];

        this.$i18n.setLocaleMessage('en', getLocaleMessages(this.activeAccountId));

        await Promise.all(settingsPromises);
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.toggleScreen(false);
      }
    },
  },
};
</script>
